import React, { memo, useEffect, useState } from 'react';
import { contactsAPI } from '../api';
import { Badge } from './ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Progress } from './ui/progress';
import { Flame, Thermometer, Snowflake, TrendingUp, Mail, MousePointer, CheckCircle } from 'lucide-react';

// Engagement level configurations
const ENGAGEMENT_LEVELS = {
  hot: {
    icon: Flame,
    color: 'bg-red-500',
    textColor: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    label: 'Hot Lead',
    description: 'Highly engaged - ready for conversion'
  },
  warm: {
    icon: Thermometer,
    color: 'bg-amber-500',
    textColor: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    label: 'Warm Lead',
    description: 'Moderately engaged - nurture further'
  },
  cold: {
    icon: Snowflake,
    color: 'bg-blue-400',
    textColor: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    label: 'Cold Lead',
    description: 'Low engagement - needs re-engagement'
  }
};

// Compact badge for contact cards
export const EngagementScoreBadge = memo(({ contactId, size = 'default' }) => {
  const [engagement, setEngagement] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEngagement = async () => {
      try {
        const res = await contactsAPI.getEngagement(contactId);
        setEngagement(res.data);
      } catch (error) {
        console.error('Failed to fetch engagement:', error);
      } finally {
        setLoading(false);
      }
    };

    if (contactId) {
      fetchEngagement();
    }
  }, [contactId]);

  if (loading || !engagement) {
    return null;
  }

  const level = ENGAGEMENT_LEVELS[engagement.level] || ENGAGEMENT_LEVELS.cold;
  const Icon = level.icon;

  if (size === 'mini') {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full ${level.bgColor} ${level.borderColor} border`}>
              <Icon className={`h-3 w-3 ${level.textColor}`} />
              <span className={`text-xs font-medium ${level.textColor}`}>{engagement.score}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Icon className={`h-4 w-4 ${level.textColor}`} />
                <span className="font-medium">{level.label}</span>
                <span className="text-muted-foreground">({engagement.score}/100)</span>
              </div>
              <p className="text-xs text-muted-foreground">{level.description}</p>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${level.bgColor} ${level.borderColor} border cursor-help`}>
            <Icon className={`h-4 w-4 ${level.textColor}`} />
            <div className="flex flex-col">
              <span className={`text-sm font-medium ${level.textColor}`}>{level.label}</span>
              <div className="flex items-center gap-1">
                <Progress value={engagement.score} className="h-1 w-16" />
                <span className="text-xs text-muted-foreground">{engagement.score}</span>
              </div>
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <div className="space-y-2">
            <p className="text-xs">{level.description}</p>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="flex items-center gap-1">
                <Mail className="h-3 w-3 text-blue-500" />
                <span>{engagement.metrics?.emails_opened || 0} opened</span>
              </div>
              <div className="flex items-center gap-1">
                <MousePointer className="h-3 w-3 text-purple-500" />
                <span>{engagement.metrics?.links_clicked || 0} clicked</span>
              </div>
              <div className="flex items-center gap-1">
                <CheckCircle className="h-3 w-3 text-green-500" />
                <span>{engagement.metrics?.conversions || 0} converted</span>
              </div>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
});

EngagementScoreBadge.displayName = 'EngagementScoreBadge';

// Automation Tags display
export const AutomationTags = memo(({ tags = [] }) => {
  if (!tags || tags.length === 0) return null;

  const TAG_COLORS = {
    green: 'bg-green-100 text-green-700 border-green-200',
    purple: 'bg-purple-100 text-purple-700 border-purple-200',
    emerald: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-100 text-amber-700 border-amber-200',
    red: 'bg-red-100 text-red-700 border-red-200',
    blue: 'bg-blue-100 text-blue-700 border-blue-200',
    pink: 'bg-pink-100 text-pink-700 border-pink-200'
  };

  return (
    <div className="flex flex-wrap gap-1">
      {tags.map((tag, idx) => (
        <TooltipProvider key={idx}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge 
                variant="outline" 
                className={`text-xs ${TAG_COLORS[tag.color] || TAG_COLORS.blue}`}
              >
                {tag.name}
              </Badge>
            </TooltipTrigger>
            <TooltipContent side="top">
              <div className="text-xs">
                <p>Added by: {tag.rule_name || tag.added_by || 'System'}</p>
                {tag.added_at && (
                  <p className="text-muted-foreground">
                    {new Date(tag.added_at).toLocaleDateString()}
                  </p>
                )}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ))}
    </div>
  );
});

AutomationTags.displayName = 'AutomationTags';

// Full Engagement Card for contact profile
export const EngagementCard = memo(({ contactId, onEvaluateRules }) => {
  const [engagement, setEngagement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [evaluating, setEvaluating] = useState(false);

  useEffect(() => {
    const fetchEngagement = async () => {
      try {
        const res = await contactsAPI.getEngagement(contactId);
        setEngagement(res.data);
      } catch (error) {
        console.error('Failed to fetch engagement:', error);
      } finally {
        setLoading(false);
      }
    };

    if (contactId) {
      fetchEngagement();
    }
  }, [contactId]);

  const handleEvaluateRules = async () => {
    setEvaluating(true);
    try {
      const res = await contactsAPI.evaluateRules(contactId);
      // Refresh engagement data after evaluation
      const engRes = await contactsAPI.getEngagement(contactId);
      setEngagement(engRes.data);
      if (onEvaluateRules) {
        onEvaluateRules(res.data);
      }
    } catch (error) {
      console.error('Failed to evaluate rules:', error);
    } finally {
      setEvaluating(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 border rounded-lg animate-pulse">
        <div className="h-4 bg-slate-200 rounded w-1/3 mb-2" />
        <div className="h-8 bg-slate-200 rounded w-1/2" />
      </div>
    );
  }

  if (!engagement) {
    return null;
  }

  const level = ENGAGEMENT_LEVELS[engagement.level] || ENGAGEMENT_LEVELS.cold;
  const Icon = level.icon;

  return (
    <div className={`p-4 rounded-lg border ${level.bgColor} ${level.borderColor}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className={`h-5 w-5 ${level.textColor}`} />
          <span className={`font-semibold ${level.textColor}`}>{level.label}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-2xl font-bold ${level.textColor}`}>{engagement.score}</span>
          <span className="text-sm text-muted-foreground">/100</span>
        </div>
      </div>

      <Progress value={engagement.score} className="h-2 mb-3" />

      <div className="grid grid-cols-4 gap-2 text-center mb-3">
        <div>
          <p className="text-lg font-semibold">{engagement.metrics?.total_campaigns || 0}</p>
          <p className="text-xs text-muted-foreground">Campaigns</p>
        </div>
        <div>
          <p className="text-lg font-semibold">{engagement.metrics?.emails_opened || 0}</p>
          <p className="text-xs text-muted-foreground">Opened</p>
        </div>
        <div>
          <p className="text-lg font-semibold">{engagement.metrics?.links_clicked || 0}</p>
          <p className="text-xs text-muted-foreground">Clicked</p>
        </div>
        <div>
          <p className="text-lg font-semibold">{engagement.metrics?.conversions || 0}</p>
          <p className="text-xs text-muted-foreground">Converted</p>
        </div>
      </div>

      {engagement.automation_tags && engagement.automation_tags.length > 0 && (
        <div className="mb-3">
          <p className="text-xs text-muted-foreground mb-1">Automation Tags</p>
          <AutomationTags tags={engagement.automation_tags} />
        </div>
      )}

      <button
        onClick={handleEvaluateRules}
        disabled={evaluating}
        className={`w-full text-sm py-1.5 rounded border ${level.borderColor} hover:bg-white/50 transition-colors disabled:opacity-50`}
      >
        {evaluating ? 'Evaluating...' : 'Run Automation Rules'}
      </button>
    </div>
  );
});

EngagementCard.displayName = 'EngagementCard';

export default EngagementScoreBadge;
